/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type AfterViewInit, type OnDestroy, type OnInit, Component, Injector, Input, ViewChild } from '@angular/core';
import { type Command, type FormGroup, type PromptConfigFull, EventService, FormSlideWindowAbstract, I18nService, LazyloadService } from '@dm/core';
import { SelectOption } from '@dm/core/types';
import { MO } from 'app/common/constants';
import { EventServiceKeyE } from 'app/common/constants/event-service-key';
import { LicenseKey } from 'app/common/constants/license-key';
import { type Lun } from 'app/common/types/mo.d';
import { SWITCH } from 'app/config';
import { type Observable, type Subscription } from 'rxjs';

import { type ErrorMsgForm } from '../../object/lun/snapshot/create-form/create-lun-snapshot-form';
import { CreatLunSnapshotFormComponent } from '../../object/lun/snapshot/create-form/create-lun-snapshot-form.component';
import { type FileSystem } from './../../../../common/types/mo.d';
import { CreateFsSnapFormComponent } from './../../object/filesystem/filesystem-snapshot/create-fs-snap-form/create-fs-snap-form.component';
import { type CreateLunSnapFormValue } from './../../object/lun/snapshot/create-form/create-lun-snapshot-form.d';
import { SnapSourceSelectTypeE } from './../../object/lun/snapshot/create-form/create-lun-snapshot-form.enum';
import { CreateSnapShotFromPageE } from './simple-create-snap.enum';
@Component({
    selector: 'app-simple-create-snap',
    templateUrl: './simple-create-snap.component.html',
    styleUrls: ['./simple-create-snap.component.scss'],
})
export class SimpleCreateSnapComponent extends FormSlideWindowAbstract implements OnInit, AfterViewInit, OnDestroy {
    /**
     * 来源页面
     */
    @Input() fromPage: CreateSnapShotFromPageE = CreateSnapShotFromPageE.LunSnapshot;

    /**
     * 源LUN选择方式，single or multi
     */
    @Input() type: SnapSourceSelectTypeE;

    /**
     * 传入的数据（LUN）
     */
    @Input() datas: Lun[] = [];

    /**
     * 租户
     */
    @Input() vstore: SelectOption;

    /**
     * 传入的数据（FS）
     */
    @Input() selectedFs: FileSystem[] = [];

    /**
     * 是否CDP（FS场景）
     */
    @Input() isCdp = false;

    /**
     * 是否显示对象类型切换，并且能添加到策略。
     */
    @Input() showObjType = false;

    /**
     * 是否显示加入cdp策略选项，并且能添加到策略。
     */
    @Input() showSchedule = false;

    /**
     * 是否显示单次快照选项
     */
    @Input() showSnap = true;

    /**
     * 是否为修改CDP策略场景
     */
    @Input() modifySchedule = false;

    /**
     * 表单组件引用
     */
    @ViewChild('lunForm') lunFormRef: CreatLunSnapshotFormComponent;
    @ViewChild('fsForm') fsFormRef: CreateFsSnapFormComponent;

    formRef: CreatLunSnapshotFormComponent | CreateFsSnapFormComponent;

    /**
     * 常量
     */
    MO = MO;
    SWITCH = SWITCH;
    SnapSourceSelectTypeE = SnapSourceSelectTypeE;

    /**
     * 保护对象，LUN or FS
     */
    protectObjTypeValue: MO.LUN | MO.FILE_SYSTEM;

    showLunObj = this.permissionService.checkShow(['lun_R'], [LicenseKey.SmartThin]);
    showFsObj = this.permissionService.hasPermission('file_system_R') && SWITCH.SUPPORT_NAS;

    /**
     * 表单
     */
    vm: FormGroup;
    errorMessage: ErrorMsgForm<CreateLunSnapFormValue>;

    i18n = {
        objectTypeLabel: this.i18nService.get('protection.protection_simplifiedMode_selectObjects_title'),
        modifySchedule: this.i18nService.get('protection.protection_simplifiedMode_modifySnapPolicy_btn'),
        addProtectTitle: this.i18nService.get('protection.protect_simplifiedMode_configProtection_btn'),
        createSnapTitle: this.i18nService.get('protection.protectionGroup_createSnapshot_button'),
        createCdpTitle: this.i18nService.get('protection.HyperCDP_task_create_title'),
        addScheduleTitle: this.i18nService.get('storage.lun_task_addToHyperCDPSchedule_tittle'),
        lunTitle: this.i18nService.get('common.lun_title'),
        fsTitle: this.i18nService.get('common.fileSystem_title'),
        saveBtnLabel: this.i18nService.get('common.confirm_lab'),
        okBtnLabel: this.i18nService.get('common.confirm_button'),
        cancelBtnLabel: this.i18nService.get('common.cancel_button'),
    };

    /**
     * 标题名称、页面宽度。和入口场景相关
     */
    title: string;
    helpLink: string;
    pageWidth: string;

    /**
     * 联机帮助
     */
    helpKeyMap = {
        lun: SWITCH.IS_SIMPLE_MODE ? 'simplifyprotectionManage' : 'lunCreateSnap',
        protect_lun: SWITCH.IS_SIMPLE_MODE ? 'simplifyprotectionManage' : 'lunprotectionCreateSnap',
        snapshot: SWITCH.IS_SIMPLE_MODE ? 'simplifyprotectionManage' : 'snapshotCreate',
        fs: SWITCH.IS_SIMPLE_MODE ? 'simplifyprotectionManage' : 'fssnapshotCreate',
        fsCdp: SWITCH.IS_SIMPLE_MODE ? 'simplifyprotectionManage' : 'fshypercdpCreate',
    };

    /**
     * 监听智能助手是否点击“会弹出侧滑框”的按钮
     */
    watchAssistant$: Subscription;

    /**
     * 极简模式，保护主页面，支持切换对象
     */
    isSimpleProtectionSupportSelectObj = false;

    constructor(
        public i18nService: I18nService,
        public lazyloadService: LazyloadService,
        private eventService: EventService,
        injector: Injector,
    ) {
        super(injector);
    }

    /**
     * 组件初始化
     */
    ngOnInit(): void {
        this.initPageWidth();

        this.pageReady = true;
        this.windowVisible = true;

        this.showSchedule = this.showSchedule && this.permissionService.checkShow(['@hyper_cdp_W'], [LicenseKey.HyperCDP]);
        this.protectObjTypeValue = this.getObjType();

        this.initTitle();
        this.initHelpLink();

        if (SWITCH.IS_SIMPLE_MODE) {
            this.watchAssistant$ = this.eventService.subscribe(EventServiceKeyE.ClickAssistantCreateBtn, () => {
                this.hide();
            });
        }

        super.ngOnInit();

        this.isSimpleProtectionSupportSelectObj = SWITCH.IS_SIMPLE_MODE &&
            this.showObjType &&
            this.showSchedule &&
            this.showSnap &&
            this.type !== SnapSourceSelectTypeE.Single;
    }

    /**
     * 初始化页面宽度
     */
    initPageWidth(): void {
        if (!SWITCH.IS_SIMPLE_MODE) {
            this.pageWidth = '8rem';
        } else if (this.showSchedule && this.showSnap) {
            this.pageWidth = '8.96rem';
        } else {
            this.pageWidth = '6.08rem';
        }
    }

    /**
     * 初始化标题
     */
    initTitle(): void {
        if (this.modifySchedule) {
            // 修改快照策略
            this.title = this.i18n.modifySchedule;
        } else if (this.showSchedule && this.showSnap) {
            // 添加保护
            this.title = this.i18n.addProtectTitle;
        } else if (this.showSchedule && !this.showSnap) {
            // 添加到快照策略
            this.title = this.i18n.addScheduleTitle;
        } else if (this.isCdp) {
            // 创建CDP
            this.title = this.i18n.createCdpTitle;
        } else {
            // 创建快照
            this.title = this.i18n.createSnapTitle;
        }
    }

    /**
     * 初始化联机帮助链接
     * @returns
     */
    initHelpLink(): void {
        if (this.fromPage === CreateSnapShotFromPageE.Lun || this.fromPage === CreateSnapShotFromPageE.ProtectLun ||
            this.fromPage === CreateSnapShotFromPageE.LunSnapshot) {
            this.helpLink = this.helpKeyMap[this.fromPage];
        } else if (this.isCdp) {
            this.helpLink = this.helpKeyMap.fsCdp;
        } else {
            this.helpLink = this.helpKeyMap.fs;
        }
    }

    ngAfterViewInit(): void {
        this.updataFormRef();
    }

    initVm(): void {
    }

    prompt(): PromptConfigFull[] {
        return this.formRef.prompt();
    }

    buildCmds(): Command[] {
        return this.formRef.buildCmds();
    }

    validate(): Observable<boolean> {
        this.updataFormRef();

        return this.formRef.validate();
    }

    isModified(): boolean {
        return true;
    }

    updataFormRef(): void {
        if (this.protectObjTypeValue === MO.LUN) {
            this.formRef = this.lunFormRef;
        } else {
            this.formRef = this.fsFormRef;
        }

        this.vm = this.formRef.vm;
    }

    getObjType(): MO.LUN | MO.FILE_SYSTEM {
        if (this.fromPage === CreateSnapShotFromPageE.Lun || this.fromPage === CreateSnapShotFromPageE.ProtectLun ||
            this.fromPage === CreateSnapShotFromPageE.LunSnapshot) {
            return MO.LUN;
        } else {
            return MO.FILE_SYSTEM;
        }
    }

    ngOnDestroy(): void {
        this.watchAssistant$?.unsubscribe();
    }
}
